<template>
  <AdminPageView :loading :loadingErrorMessage :title="`${travel?.name ? travel?.name : ''}${travel?.company ? ` (${travel?.company})` : ''}`">
    <template #actions>
      <button class="btn btn-outline-success text-nowrap" @click="saveTravel" :class="{ 'disabled': isSubmitting }"><i class="fa-solid fa-floppy-disk pe-2"></i> Enregistrer</button>
    </template>
    <template #stickyMenu>
      <StepperComponent class="d-none d-md-flex"
      @on-step-changed="stepChanged"
      :steps="[{
        title: 'Informations',
        icon: 'fa-solid fa-info',
        anchor: '#infos'
      }, {
        title: 'Fichiers',
        icon: 'fa-solid fa-file',
        anchor: '#files'
      }, {
        title: 'Coûts',
        icon: 'fa-solid fa-coins',
        anchor: '#cost'
      }, {
        title: 'Paiement',
        icon: 'fa-solid fa-credit-card',
        anchor: '#payment'
      }, {
        title: 'Transports',
        icon: 'fa-solid fa-plane',
        anchor: '#transport'
      },
      {
        title: 'Transferts',
        icon: 'fa-solid fa-bus',
        anchor: '#transfert'
      },
      {
        title: 'Hôtels',
        icon: 'fa-solid fa-hotel',
        anchor: '#hotels'
      },
      {
        title: 'Options',
        icon: 'fa-solid fa-person-snowboarding',
        anchor: '#options'
      },
      {
        title: 'Infos Sup',
        icon: 'fa-solid fa-shirt',
        anchor: '#infoFields'
      },
      {
        title: 'Documents',
        icon: 'fa-solid fa-file-arrow-up',
        anchor: '#documents'
      },
      {
        title: 'Pages',
        icon: 'fa-solid fa-file-pen',
        anchor: '#pages'
      },
      {
        title: 'Messages',
        icon: 'fa-solid fa-message',
        anchor: '#messages'
      },
      ]" />
    </template>
    <div>
      <AdminTravelEditInfos anchor="infos" v-model="travel" v-model:validation-schema="infoValidationSchema" />
      <AdminTravelEditFiles anchor="files" v-model="travel" class="mt-3" v-model:validation-schema="filesValidationSchema" />
      <AdminTravelEditCost anchor="cost" v-model="travel" class="mt-3" v-model:validation-schema="costValidationSchema" />
      <AdminTravelEditPayment anchor="payment" v-model="travel" class="mt-3" v-model:validation-schema="paymentValidationSchema" />
      <AdminTravelEditTransport anchor="transport" v-model="travel" class="mt-3" v-model:validation-schema="transportValidationSchema" />
      <AdminTravelEditTransfert anchor="transfert" v-model="travel" class="mt-3" v-model:validation-schema="transfertValidationSchema" />
      <AdminTravelEditHotel anchor="hotels" v-model="travel" class="mt-3" v-model:validation-schema="hotelValidationSchema" />
      <AdminTravelEditOption anchor="options" v-model="travel" class="mt-3" v-model:validation-schema="optionValidationSchema" />
      <AdminTravelEditMoreInfos anchor="infoFields" v-model="infoFields" class="mt-3" v-model:validation-schema="moreInfosValidationSchema" />
      <AdminTravelEditDocuments anchor="documents" v-model="documents" class="mt-3" v-model:validation-schema="documentsValidationSchema" />
      <AdminTravelEditPages anchor="pages" v-model="travel" class="mt-3" v-model:validation-schema="pagesValidationSchema" />
      <AdminTravelEditMessage anchor="messages" v-model="travel" class="mt-3" v-model:validation-schema="messageValidationSchema" />
    </div>
  </AdminPageView>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import * as zod from 'zod';
import { IdNotDefined, useConfirmDialog, ScrollToId } from '@/compositions/helper';

import AdminApi from '@/api/adminApi'
import AdminPageView from '@/views/admin/AdminPageView.vue';
import { useRouter,onBeforeRouteLeave } from 'vue-router'

import { useNotificationsStore } from '@/stores/notificationStore';
import AdminTravelEditInfos from '@/views/admin/travel/AdminTravelEditInfos.vue';
import AdminTravelEditCost from '@/views/admin/travel/AdminTravelEditCost.vue';
import AdminTravelEditPayment from '@/views/admin/travel/AdminTravelEditPayment.vue';
import AdminTravelEditTransport from '@/views/admin/travel/AdminTravelEditTransport.vue';
import AdminTravelEditTransfert from '@/views/admin/travel/AdminTravelEditTransfert.vue';
import AdminTravelEditMoreInfos from '@/views/admin/travel/AdminTravelEditMoreInfos.vue';
import AdminTravelEditDocuments from '@/views/admin/travel/AdminTravelEditDocuments.vue';
import AdminTravelEditOption from '@/views/admin/travel/AdminTravelEditOption.vue';
import AdminTravelEditHotel from '@/views/admin/travel/AdminTravelEditHotel.vue';
import AdminTravelEditFiles from '@/views/admin/travel/AdminTravelEditFiles.vue';
import AdminTravelEditMessage from '@/views/admin/travel/AdminTravelEditMessage.vue';
import AdminTravelEditPages from '@/views/admin/travel/AdminTravelEditPages.vue';

const notificationsStore = useNotificationsStore();
const { confirmDialog } = useConfirmDialog();
const router = useRouter()

const loading = ref(true);
const loadingErrorMessage = ref<string|undefined>(undefined);
const props = defineProps<{ id?: number }>();

const infoValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const costValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const paymentValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const transportValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const transfertValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const optionValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const hotelValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const moreInfosValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const documentsValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const filesValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const messageValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })
const pagesValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} })


const validationSchema = computed(() => {
  const mergedSchemas = zod.object({
      id: zod.number().nonnegative(),
      ...zod.object(infoValidationSchema.value.shape).shape,
      ...zod.object(costValidationSchema.value.shape).shape,
      ...zod.object(paymentValidationSchema.value.shape).shape,
      ...zod.object(transportValidationSchema.value.shape).shape,
      ...zod.object(transfertValidationSchema.value.shape).shape,
      ...zod.object(optionValidationSchema.value.shape).shape,
      ...zod.object(hotelValidationSchema.value.shape).shape,
      ...zod.object(moreInfosValidationSchema.value.shape).shape,
      ...zod.object(documentsValidationSchema.value.shape).shape,
      ...zod.object(filesValidationSchema.value.shape).shape,
      ...zod.object(messageValidationSchema.value.shape).shape,
      ...zod.object(pagesValidationSchema.value.shape).shape,
    });

  return toTypedSchema(mergedSchemas);
});

const anchorMapping = computed(() => {
  return {
    ...infoValidationSchema.value.anchors,
    ...costValidationSchema.value.anchors,
    ...paymentValidationSchema.value.anchors,
    ...transportValidationSchema.value.anchors,
    ...transfertValidationSchema.value.anchors,
    ...optionValidationSchema.value.anchors,
    ...hotelValidationSchema.value.anchors,
    ...moreInfosValidationSchema.value.anchors,
    ...documentsValidationSchema.value.anchors,
    ...filesValidationSchema.value.anchors,
    ...messageValidationSchema.value.anchors,
    ...pagesValidationSchema.value.anchors,
  };
});

const defaultTravel = <AdminTravel><unknown>{
  id: IdNotDefined,
  type: TravelDbTypeEnum.Seminaire,
  priceTTC: false,
  organisationMemberCount: 0,
  emailContact: 'mynemo@nemoprod.fr',
  birthDateRequired: true,
  addressRequired: false,
  passeportRequired: true,
  passeportSixMonthsRequired: false,
  estaRequired: false,
  userStatusRequired: false,
  canRefuse: false,
  invoiceEnabled: false,
  companionsEnabled: true,
  paymentEnabled: false,
  nationalityRequired: false,
  packageMandatory: false,
  emergencyContactRequested: false,
  titleBannerAlignment: TravelDbTitleBannerAlignmentEnum.Center,
}

const {
  handleSubmit,
  isSubmitting,
  meta,
  resetForm,
  defineField,
  values: travel
} = useForm<AdminTravel>({
  initialValues: defaultTravel,
  validationSchema: validationSchema
});
const [infoFields] = defineField('infoFields');
const [documents] = defineField('documents');

const saveTravel = async () => {
  handleSubmit(async (values,actions) => {
    await AdminApi.addUpdateTravel(values).then((response) => {
      if(!props.id || props.id === IdNotDefined) {
        resetForm({values: response.data});
        router.replace({ name: 'admin-edit-travel', params: { id: response.data.id } });
        notificationsStore.notifySuccess('Événement créé avec succès.');
      } else {
        actions.resetForm({values: response.data});
        notificationsStore.notifySuccess('Événement mis à jour avec succès.');
      }

    }).catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors de la mise à jour de l\'événement',error.message);
    });
  },(result) => {
      const errors = Object.keys(result.errors)
      .map((key:string) => {
        let anchor:string|undefined = undefined;
        if(anchorMapping.value && anchorMapping.value[key]){
          anchor = anchorMapping.value[key]
        } else {
          const anchorMappingKeys = Object.keys(anchorMapping.value);
          const match = anchorMappingKeys.find(value =>
          {
            return new RegExp(`^${value.replaceAll('[', '\\[').replaceAll(']', '\\]').replaceAll(".", '\.')}$`).test(key)
          });
          if (match) {
            anchor = anchorMapping.value[match];
            if (anchor === 'self' && key.lastIndexOf('.') !== -1)
            {
              anchor = key.substring(0,key.lastIndexOf('.'));
            }
          }
        }

        return anchor ? `<a class="text-decoration-none text-reset" href="#${anchor}">${result.errors[key as keyof typeof result.errors]}</a>` : result.errors[key as keyof typeof result.errors]
      });

      notificationsStore.notifyError(errors.slice(0,5).join('<br>'));
  })()
}

const load = () => {
  if (props.id) {
    AdminApi.getTravel(props.id).then((response) => {
      resetForm({values: response.data});
    }).catch((error: Error) => {
      loadingErrorMessage.value = 'Impossible de charger l\'événement';
      console.error('Error loading travel:', error);
    }).finally(() => {
      loading.value = false;
    });
  } else {
    resetForm({ values: defaultTravel });
    loading.value = false;
  }
};

const stepChanged = (newIndex: number, newStep: StepperElement) => {
  if(newStep.anchor) {
    ScrollToId(newStep.anchor.replace('#',''));
  }
};

onMounted(() => {
  load();
});

onBeforeRouteLeave(() => {
  if(meta.value.dirty) {
    return confirmDialog("Vous avez des modifications non enregistrées.<br>Voulez-vous vraiment quitter cette page ?");
  } else {
    return true
  }
});

watch(() => props.id, () => {
    load()
});
</script>
